import { useMemo } from 'react';
import ReactSelect from 'components/base/ReactSelect';
import useGetProductCategory from 'pages/apps/hooks/useGetProductCategory';
import { collapseCategorySearch } from 'utils/categorySearch';
import { TProductCategoryDetails } from 'utils/types';

export type CategorySelectOption = {
  value: number;
  label: string;
  category: TProductCategoryDetails;
};

type CategorySelectProps = {
  value: number | null | undefined;
  onChange: (
    id: number | null,
    category: TProductCategoryDetails | null
  ) => void;
  leavesOnly?: boolean;
  excludeId?: number;
  isClearable?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
  valueLabel?: string;
};

const CategorySelect = ({
  value,
  onChange,
  leavesOnly = false,
  excludeId,
  isClearable = false,
  isInvalid = false,
  isDisabled = false,
  placeholder = 'Search category',
  className,
  valueLabel
}: CategorySelectProps) => {
  const { productCategories } = useGetProductCategory(leavesOnly);

  const options = useMemo<CategorySelectOption[]>(() => {
    const mapped = productCategories
      .filter(c => c.productCategoryId !== excludeId)
      .map(c => ({
        value: c.productCategoryId,
        label: c.fullPath || c.name,
        category: c
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (
      value != null &&
      valueLabel &&
      !mapped.some(option => option.value === value)
    ) {
      return [
        {
          value,
          label: valueLabel,
          category: {
            productCategoryId: value,
            name: valueLabel
          }
        },
        ...mapped
      ];
    }

    return mapped;
  }, [productCategories, excludeId, value, valueLabel]);

  const selected =
    value == null ? null : options.find(option => option.value === value) ?? null;

  return (
    <ReactSelect
      className={className}
      classNamePrefix="react-select"
      options={options}
      value={selected}
      placeholder={placeholder}
      isSearchable
      isClearable={isClearable}
      isDisabled={isDisabled}
      closeMenuOnSelect
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        menuPortal: base => ({ ...base, zIndex: 9999 })
      }}
      aria-invalid={isInvalid}
      filterOption={(option, input) => {
        const needle = collapseCategorySearch(input);
        if (!needle) return true;
        return collapseCategorySearch(option.label).includes(needle);
      }}
      onChange={option => {
        const next = option as CategorySelectOption | null;
        onChange(next?.value ?? null, next?.category ?? null);
      }}
    />
  );
};

export default CategorySelect;
